// Layout.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { debounce } from 'lodash';
import { Box, AppBar, Toolbar, Typography, Button, Avatar, Menu, MenuItem, IconButton, Divider, Grid, Link, useTheme } from "@mui/material";
import {
	AccountCircle, Language, Brightness4, Brightness7, Mic,
	MicOff,
	ZoomIn,
	ZoomOut,
	RestartAlt,
	Menu as MenuIcon
} from "@mui/icons-material";
import { useNavigate, Outlet } from "react-router-dom";
import Navbar, { getNavbarPosition } from "../components/common/navbar-templates/Navbar";
import ExpandableFooter from "../components/common/ExpandableFooter";
import StaticFooter from "../components/common/StaticFooter";
import { StyledIconButton } from '../components/common/StyledIconButton';
import { useAuth } from "../hooks/useAuth";
import { setLanguage } from "../store/slices/database/translationSlice";
import { toggleTheme } from "../store/slices/themeSlice";
import { useDispatch, useSelector } from "react-redux";
import { persistor } from "../store";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

export default function Layout({ children }) {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { t } = useTranslation();
	const [readingEnabled, setReadingEnabled] = useState(JSON.parse(localStorage.getItem("readingEnabled")) || false);
	const [zoomLevel, setZoomLevel] = useState(parseFloat(localStorage.getItem("zoomLevel")) || 1);
	const { currentLanguage } = useSelector((state) => state.translations);
	const { mode: themeMode } = useSelector((state) => state.theme);
	const { data: domainData } = useSelector((state) => state.domainConfig);

	const keycloakAuth = useAuth();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const [langAnchorEl, setLangAnchorEl] = useState(null);
	const [mobileAnchorEl, setMobileAnchorEl] = useState(null);

	// Speech synthesis setup
	useEffect(() => {
		// Cancel any ongoing speech when component unmounts
		return () => {
			window.speechSynthesis.cancel();
		};
	}, []);


	// Setup hover-to-read functionality with debouncing
	const handleMouseOverCore = useCallback((e) => {
		if (!readingEnabled) return;

		// Get the text content of the element being hovered
		const element = e.target;
		let textToRead = "";

		// Check if it's a text node or has text content
		if (element.textContent && element.textContent.trim()) {
			// Get only the direct text of this element, not children
			textToRead = Array.from(element.childNodes)
				.filter(node => node.nodeType === Node.TEXT_NODE)
				.map(node => node.textContent.trim())
				.join(" ");

			// If no direct text, get the full text content
			if (!textToRead) {
				textToRead = element.textContent.trim();
			}
		}

		// Read the text if it exists and is not too long
		if (textToRead && textToRead.length > 0 && textToRead.length < 200) {
			// Cancel any ongoing speech
			window.speechSynthesis.cancel();

			// Create a new speech utterance
			const utterance = new SpeechSynthesisUtterance(textToRead);

			// Set the language based on current language
			utterance.lang = currentLanguage === 'ar' ? 'ar-SA' : 'en-US';
			utterance.rate = 0.9; // Slightly slower for better clarity
			utterance.pitch = 1;
			utterance.volume = 1;

			// Speak the text
			window.speechSynthesis.speak(utterance);
		}
	}, [readingEnabled, currentLanguage]);

	// Create debounced version of the handler (300ms delay)
	const handleMouseOver = useMemo(
		() => debounce(handleMouseOverCore, 300),
		[handleMouseOverCore]
	);

	useEffect(() => {
		if (readingEnabled) {
			// Add event listener to the entire document
			document.addEventListener('mouseover', handleMouseOver);
		}

		// Cleanup
		return () => {
			document.removeEventListener('mouseover', handleMouseOver);
			handleMouseOver.cancel(); // Cancel any pending debounced calls
			window.speechSynthesis.cancel();
		};
	}, [readingEnabled, handleMouseOver]);

	const handleMenu = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleLangMenu = (event) => {
		setLangAnchorEl(event.currentTarget);
	};

	const handleLangClose = () => {
		setLangAnchorEl(null);
	};

	const handleMobileMenuOpen = (event) => {
		setMobileAnchorEl(event.currentTarget);
	};

	const handleMobileMenuClose = () => {
		setMobileAnchorEl(null);
	};

	const handleLanguageChange = (lng) => {
		//localStorage.setItem("language", lng);
		dispatch(setLanguage(lng));
		handleLangClose();
	};

	const handleThemeToggle = () => {
		dispatch(toggleTheme());
	};

	const handleLogout = () => {
		handleClose();

		// Purge all persisted state from localStorage
		// This clears selectedCompany, menuConfig, domainConfig, and theme
		persistor.purge().then(() => {
			// Only logout after purge completes
			keycloakAuth.logout();
		});
	};

	const handleSwitchCompany = () => {
		navigate("/userCompanies");
		handleClose();
	};

	const handleAccountManagement = () => {
		keycloakAuth.keycloak.accountManagement();
		handleClose();
	};


	// Toggle reading mode
	const handleToggleReading = () => {
		const newValue = !readingEnabled;
		setReadingEnabled(newValue);
		localStorage.setItem("readingEnabled", JSON.stringify(newValue));

		// Cancel any ongoing speech when toggling off
		if (!newValue) {
			window.speechSynthesis.cancel();
		}
	};

	// Zoom in functionality
	const handleZoomIn = () => {
		const newZoom = Math.min(zoomLevel + 0.1, 2); // Max zoom 200%
		setZoomLevel(newZoom);
		// if we want to persist let's say upon refresh
		//localStorage.setItem("zoomLevel", newZoom.toString());
	};

	// Zoom out functionality
	const handleZoomOut = () => {
		const newZoom = Math.max(zoomLevel - 0.1, 0.5); // Min zoom 50%
		setZoomLevel(newZoom);
		// if we want to persist let's say upon refresh
		//localStorage.setItem("zoomLevel", newZoom.toString());
	};

	const handleZoomReset = () => {
		setZoomLevel(1);
		// if we want to persist let's say upon refresh
		//localStorage.setItem("zoomLevel", "1");
	};

	const user = keycloakAuth.user;

	const languageMap = {
		en: "English",
		ar: "العربية",
	};

	const navbarTemplate = "default";
	const navbarPosition = getNavbarPosition(navbarTemplate);

	const footerlist1 = [
		{ title: t("الرؤية والرسالة"), to: "https://mwan.gov.sa/vision-mission", openInNewTab: true },
		{ title: t("أهداف المركز"), to: "https://mwan.gov.sa/goals", openInNewTab: true },
		{ title: t("مهام المركز"), to: "https://mwan.gov.sa/tasks", openInNewTab: true },
		{ title: t("قيم المركز"), to: "https://mwan.gov.sa/values", openInNewTab: true },
	];
	const footerlist2 = [
		{ title: t("منصة التراخيص والتصاريح"), to: "https://mwan.gov.sa/permit-and-license-platform", openInNewTab: true },
		{ title: t("الاشتراطات والمتطلبات"), to: "https://mwan.gov.sa/conditions-and-requirements", openInNewTab: true },
	];
	const footerlist3 = [
		{ title: t("وزارة البيئة والمياه والزراعة"), to: "https://www.mewa.gov.sa/ar/Pages/default.aspx", openInNewTab: true },
		{ title: t("صندوق البيئة"), to: "https://ef.gov.sa/ar/Pages/default.aspx", openInNewTab: true },
		{ title: t("دليل المستخدم"), to: "/manuals", openInNewTab: false },
	];

	const renderNavbar = () => (
		<Box sx={{ display: { xs: 'none', lg: 'flex' }, height: '100%', alignItems: 'center' }}>
			<Navbar template={navbarTemplate} />
		</Box>
	);
	const scaledStyle = {
		transform: `scale(${zoomLevel})`,
		transformOrigin: "top left",
		width: `${100 / zoomLevel}%`,
		// <-- important: make scaled wrapper tall enough so after scale it fills viewport
		height: `calc(100dvh / ${zoomLevel})`,
		// prevent horizontal scrollbar introduced by the scale
		overflowX: "hidden",
	};
	return (
		<Box sx={scaledStyle}>
			<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				{/* AppBar */}
				<AppBar position="static" color="inherit" sx={{ bgcolor: "primary.contrastText", flexShrink: 0 }}>
					<Toolbar
						sx={{
							justifyContent: "space-between",
							alignItems: "center",
							height: "64px", // Set explicit height
							minHeight: "64px !important", // Override MUI default minHeight
						}}
					>
						{/* Mobile Drawer */}
						<Navbar
							isMobile={true}
							mobileOpen={Boolean(mobileAnchorEl)}
							onMobileClose={handleMobileMenuClose}
							anchor={theme.direction === 'rtl' ? 'right' : 'left'}
						/>

						{/* LEFT SIDE */}
						{navbarPosition === "left" ? (
							<Box sx={{ display: "flex", alignItems: "stretch", gap: { xs: 1, md: 3 }, height: "100%" }}>
								{/* Hamburger icon only visible on mobile */}
								<IconButton
									color="primary"
									aria-label="open drawer"
									onClick={handleMobileMenuOpen}
									edge="start"
									sx={{ display: { xs: 'flex', lg: 'none' }, alignSelf: 'center' }}
								>
									<MenuIcon />
								</IconButton>

								<Box
									component="img"
									src={domainData?.logo}
									alt="Company Logo"
									sx={{
										height: 40,
										width: "auto",
										maxWidth: 200,
										objectFit: "contain",
										cursor: "pointer",
										alignSelf: "center",
									}}
								/>
								{renderNavbar()}
							</Box>
						) : (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
								{/* Hamburger icon only visible on mobile */}
								<IconButton
									color="primary"
									aria-label="open drawer"
									onClick={handleMobileMenuOpen}
									edge="start"
									sx={{ display: { xs: 'flex', lg: 'none' } }}
								>
									<MenuIcon />
								</IconButton>

								<Box
									component="img"
									src={domainData?.logo}
									alt="Company Logo"
									sx={{
										height: 40,
										width: "auto",
										maxWidth: 200,
										objectFit: "contain",
										cursor: "pointer",
										flexShrink: 0,
									}}
								/>
							</Box>
						)}

						{/* CENTER */}
						{navbarPosition === "center" && <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>{renderNavbar()}</Box>}

						{/* RIGHT SIDE */}
						<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
							{navbarPosition === "right" && <Box sx={{ mr: 2 }}>{renderNavbar()}</Box>}

							{keycloakAuth.isAuthenticated ? (
								<>
									<IconButton onClick={handleThemeToggle} color="primary" aria-label="toggle dark mode">
										{themeMode === "dark" ? <Brightness7 /> : <Brightness4 />}
									</IconButton>

									<Button
										color="primary"
										onClick={handleLangMenu}
										sx={{
											borderColor: "rgba(255,255,255,0.5)",
											border: "1px solid",
											"&:hover": {
												borderColor: "white",
												bgcolor: "rgba(255,255,255,0.1)",
											},
											display: "flex",
											gap: 1,
											flexDirection: "row",
											minWidth: { xs: 'auto', sm: 64 },
											px: { xs: 1, sm: 2 }
										}}
									>
										<Language />
										<Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
											{languageMap[currentLanguage] || "English"}
										</Box>
									</Button>

									<Menu
										anchorEl={langAnchorEl}
										open={Boolean(langAnchorEl)}
										onClose={handleLangClose}
										anchorOrigin={{
											vertical: "bottom",
											horizontal: "right",
										}}
										transformOrigin={{
											vertical: "top",
											horizontal: "right",
										}}
									>
										<MenuItem onClick={() => handleLanguageChange("en")}>English</MenuItem>
										<MenuItem onClick={() => handleLanguageChange("ar")}>العربية</MenuItem>
									</Menu>

									<Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
										{t("Welcome")}, {user?.firstName} {user?.lastName || user?.username}
									</Typography>

									<IconButton size="large" aria-label="account of current user" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleMenu} color="primary" sx={{ p: { xs: 0.5, sm: 1.5 } }}>
										<Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>{user?.firstName?.charAt(0) || user?.username?.charAt(0) || "U"}</Avatar>
									</IconButton>

									<Menu
										id="menu-appbar"
										anchorEl={anchorEl}
										anchorOrigin={{
											vertical: "bottom",
											horizontal: "right",
										}}
										transformOrigin={{
											vertical: "top",
											horizontal: "right",
										}}
										open={Boolean(anchorEl)}
										onClose={handleClose}
										slotProps={{
											paper: {
												sx: {
													backgroundColor: "primary.contrastText",
													color: "primary.main",
												},
											},
										}}
									>
										{/* <MenuItem onClick={handleAccountManagement}>
											<AccountCircle sx={{ mr: 2 }} />
											Manage Account
										</MenuItem> */}
										<Divider />
										<MenuItem onClick={handleSwitchCompany}>{t("Switch Company")}</MenuItem>
										<Divider />
										<MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
											{t("Logout")}
										</MenuItem>
									</Menu>
								</>
							) : (
								<>
									<Button
										color="primary"
										onClick={handleLangMenu}
										startIcon={<Language />}
										sx={{
											borderColor: "rgba(255,255,255,0.5)",
											border: "1px solid",
											"&:hover": {
												borderColor: "white",
												bgcolor: "rgba(255,255,255,0.1)",
											},
										}}
									>
										{languageMap[currentLanguage] || "English"}
									</Button>

									<Menu
										anchorEl={langAnchorEl}
										open={Boolean(langAnchorEl)}
										onClose={handleLangClose}
										anchorOrigin={{
											vertical: "bottom",
											horizontal: "right",
										}}
										transformOrigin={{
											vertical: "top",
											horizontal: "right",
										}}
									>
										<MenuItem onClick={() => handleLanguageChange("en")}>English</MenuItem>
										<MenuItem onClick={() => handleLanguageChange("ar")}>العربية</MenuItem>
									</Menu>

									<Button
										color="primary"
										variant="outlined"
										onClick={() => navigate("/login")}
										sx={{
											borderColor: "rgba(255,255,255,0.5)",
											"&:hover": {
												borderColor: "white",
												bgcolor: "rgba(255,255,255,0.1)",
											},
										}}
									>
										Login
									</Button>
								</>
							)}
						</Box>
					</Toolbar>
				</AppBar>

				{/* Scrollable Content Area */}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						minHeight: 0,
						//height: "100%"
						//height: "calc(100% - 64px)"
						//height: "calc(100dvh - 64px)"
					}}
				>
					{/* Main Content - this will grow to fill available space */}
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							overflow: "auto",
							//height: "100%"
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box sx={{ flex: 1 }}>
							<Outlet />
						</Box>

						<StaticFooter sx={{ bgcolor: "primary.main", color: "primary.contrastText", mt: 'auto' }}>
							<Grid container spacing={3}>
								{/* <Grid item xs={12} sm={6} md={3}>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
										<Link
											sx={{
												color: "inherit",
												textDecoration: "none",
												textTransform: "uppercase",
												fontSize: "1.2rem",
											}}
										>
											{t("Overview")}
										</Link>
										<Divider
											sx={{
												color: "inherit",
												borderColor: "white",
											}}
										/>
										{footerlist1.map(({ title, to, isDownload = false, openInNewTab = false }) => (
											<Link
												key={to}
												target={isDownload ? "_blank" : (openInNewTab ? "_blank" : undefined)}
												download={isDownload || undefined}
												component={RouterLink}
												to={to}
												sx={{
													color: "inherit",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{title}
											</Link>
										))}
									</Box>
								</Grid>

								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
										<Link
											sx={{
												color: "inherit",
												textDecoration: "none",
												textTransform: "uppercase",
												fontSize: "1.2rem",
											}}
										>
											{t("نظام التراخيص")}
										</Link>
										<Divider
											sx={{
												color: "inherit",
												borderColor: "white",
											}}
										/>
										{footerlist2.map(({ title, to, isDownload = false, openInNewTab = false }) => (
											<Link
												key={to}
												target={isDownload ? "_blank" : (openInNewTab ? "_blank" : undefined)}
												download={isDownload || undefined}
												component={RouterLink}
												to={to}
												sx={{
													color: "inherit",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{title}
											</Link>
										))}
									</Box>
								</Grid>

								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
										<Link
											sx={{
												color: "inherit",
												textDecoration: "none",
												textTransform: "uppercase",
												fontSize: "1.2rem",
											}}
										>
											{t("روابط هامة")}
										</Link>
										<Divider
											sx={{
												color: "inherit",
												borderColor: "white",
											}}
										/>
										{footerlist3.map(({ title, to, isDownload = false, openInNewTab = false }) => (
											<Link
												key={to}
												target={isDownload ? "_blank" : (openInNewTab ? "_blank" : undefined)}
												download={isDownload || undefined}
												component={RouterLink}
												to={to}
												sx={{
													color: "inherit",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{title}
											</Link>
										))}
									</Box>
								</Grid>

								<Grid item xs={12} sm={6} md={3}>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
										<Link
											sx={{
												color: "inherit",
												textDecoration: "none",
												textTransform: "uppercase",
												fontSize: "1.2rem",
											}}
										>
											{t("Follow us")}
										</Link>
										<Divider
											sx={{
												color: "inherit",
												borderColor: "white",
											}}
										/>
										<Link
											sx={{
												color: "inherit",
												textDecoration: "none",
												textTransform: "uppercase",
												fontSize: "1.2rem",
											}}
										>
											{t("امكانية الوصول")}
										</Link>
										<Divider
											sx={{
												color: "inherit",
												borderColor: "white",
												textDecoration: "none",
												"&:hover": { textDecoration: "underline" },
											}}
										/>

										<Box sx={{ display: "flex", gap: 1 }}>
											<StyledIconButton onClick={handleToggleReading}>
												{readingEnabled ? <Mic /> : <MicOff />}
											</StyledIconButton>
											<StyledIconButton onClick={handleZoomIn}>
												<ZoomIn />
											</StyledIconButton>
											<StyledIconButton onClick={handleZoomOut}>
												<ZoomOut />
											</StyledIconButton>
											{zoomLevel !== 1 && (
												<StyledIconButton onClick={handleZoomReset}>
													<RestartAlt />
												</StyledIconButton>
											)}
										</Box>
									</Box>
								</Grid> */}

								<Grid item xs={12}>
									<Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
										<Typography sx={{ mt: 2, textAlign: "center" }}>{t("Copyright © 2026 INSIGHT. All rights reserved.")}</Typography>

										<Box sx={{ flex: 1 }} />
										<Box
											component="img"
											src={domainData?.logo}
											alt="Company Logo"
											sx={{
												height: 55,
												width: "auto",
												objectFit: "contain",
												cursor: "pointer",
												// filter: "brightness(0) invert(1)",
											}}
										/>
									</Box>
								</Grid>
							</Grid>
						</StaticFooter>
					</Box>

					{/* Expandable Footer - at the bottom of scrollable area */}
					{/* <ExpandableFooter
                    title="Details"
                    initiallyExpanded={true}
                    showCloseButton={true}
                    onToggle={(isOpen) => console.log('Footer is:', isOpen)}
                    sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
                >
                    <Grid container spacing={3}>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Link
                                    component={RouterLink}
                                    to="/privacypolicy"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Privacy policy")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/faq"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("FAQ")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/contact"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Contact us")}
                                </Link>
                            </Box>
                        </Grid>


                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Link
                                    component={RouterLink}
                                    to="/privacypolicy"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Privacy policy")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/faq"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("FAQ")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/contact"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Contact us")}
                                </Link>
                            </Box>
                        </Grid>


                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Link
                                    component={RouterLink}
                                    to="/privacypolicy"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Privacy policy")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/faq"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("FAQ")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/contact"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Contact us")}
                                </Link>
                            </Box>
                        </Grid>


                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Link
                                    component={RouterLink}
                                    to="/privacypolicy"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Privacy policy")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/faq"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("FAQ")}
                                </Link>

                                <Link
                                    component={RouterLink}
                                    to="/contact"
                                    sx={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {t("Contact us")}
                                </Link>
                            </Box>
                        </Grid>


                        <Grid item xs={12}>
                            <Typography sx={{ mt: 2, textAlign: 'center' }}>
                                {t("All rights reserved 2025 National Center for Waste Management | Kingdom of Saudi Arabia")}
                            </Typography>
                        </Grid>
                    </Grid>
                </ExpandableFooter> */}
				</Box>
			</Box>
		</Box>

	);
}